import { Player, OvertakeEvent } from '../types/game';
import { gameRules } from './gameRules';

type OvertakeOptions = {
  ignoreOver900?: boolean;
  allowNoEntry?: boolean;
  includeProtectedVictims?: boolean;
  ignoreProtectedVictims?: boolean;
  /**
   * Gracze, którzy w tej turze byli już wyprzedzeni i nie mogą oberwać drugi raz.
   * Wyklucza ich z kaskady od razu, a nie po fakcie – filtrowanie wyniku zmieniłoby
   * bonus, bo każdy etap kaskady wydłuża zasięg następnego.
   */
  excludeVictimIds?: ReadonlySet<string>;
};

export const OVERTAKE_PROJECTION: OvertakeOptions = {
  ignoreOver900: true,
  allowNoEntry: true,
  includeProtectedVictims: false,
  ignoreProtectedVictims: true,
};

export const OVERTAKE_ACTUAL: OvertakeOptions = {
  allowNoEntry: true,
  ignoreProtectedVictims: true,
};

// Wydzielona logika wyprzedzania z GameContext (czysta funkcja)
/**
 * Calculates overtake bonuses and penalties based on projected score.
 */
export function calculateOvertaking(
  players: Player[],
  playerId: string,
  baseScore: number,
  options?: OvertakeOptions
): OvertakeEvent | null {
  const protectedScore = gameRules.overtake.protectedScore;
  const bonusValue = gameRules.bonuses.overtake;
  const penaltyValue = gameRules.penalties.overtake;
  const playerIndex = players.findIndex(p => p.id === playerId);
  if (playerIndex === -1) return null;
  const currentPlayer = players[playerIndex];
  const oldScore = currentPlayer.score;
  // Po 900 punktach nie przyznajemy bonusów/kary za wyprzedzenie
  if (!options?.ignoreOver900 && oldScore >= protectedScore) {
    return null;
  }

  let targetScore = baseScore;
  const overtaken: Player[] = [];
  const stages: { players: Player[]; cumulativeBonus: number; resultingScore: number }[] = [];
  const takenIds = new Set<string>(options?.excludeVictimIds ?? []);
  const hasEntered = (p: Player) => Boolean(p.hasFirstEntry || (options?.allowNoEntry && p.score > 0));
  const allowProtectedVictims = options?.ignoreProtectedVictims ? false : options?.includeProtectedVictims === true;
  const isEligibleVictim = (p: Player) =>
    p.id !== playerId &&
    !takenIds.has(p.id) &&
    hasEntered(p) &&
    (allowProtectedVictims || p.score < protectedScore) &&
    p.score > oldScore &&
    p.score < targetScore;

  for (let safety = 0; safety < players.length; safety++) {
    const newly = players.filter(isEligibleVictim);
    if (newly.length === 0) break;
    newly.forEach(p => { takenIds.add(p.id); overtaken.push(p); });
    const cumulativeBonus = overtaken.length * bonusValue;
    targetScore = baseScore + cumulativeBonus;
    stages.push({ players: newly, cumulativeBonus, resultingScore: targetScore });
  }
  if (overtaken.length === 0) return null;
  return { overtaker: currentPlayer, overtaken, bonusPoints: bonusValue * overtaken.length, penaltyPoints: penaltyValue, stages };
}
